import React from "react";
import { motion } from "framer-motion";
import { Clock, Bot, CheckCircle, BarChart, Zap, Code, ShieldCheck, Database } from "lucide-react";

const MetricsDashboard = () => {
  const metrics = [
    {
      value: "100%",
      label: "Lead Qualification Automated",
      description: "AI-driven lead qualification via OpenAI voice & chat agents.",
      icon: <Bot className="w-6 h-6 text-cyan-400" />,
      color: "from-cyan-500/20 to-blue-500/20",
      glow: "shadow-cyan-500/10"
    },
    {
      value: "30+",
      label: "Hours Saved / Week",
      description: "Eliminating manual CRM data entry and appointment scheduling.",
      icon: <Clock className="w-6 h-6 text-emerald-400" />,
      color: "from-emerald-500/20 to-teal-500/20",
      glow: "shadow-emerald-500/10"
    },
    {
      value: "20+",
      label: "Enterprise Solutions",
      description: "Delivering client solutions for HubSpot, Salesforce & NetSuite.",
      icon: <CheckCircle className="w-6 h-6 text-indigo-400" />,
      color: "from-indigo-500/20 to-purple-500/20",
      glow: "shadow-indigo-500/10"
    },
    {
      value: "95%",
      label: "Manual Work Reduction",
      description: "Seamless data synchronization across multiple systems.",
      icon: <BarChart className="w-6 h-6 text-pink-400" />,
      color: "from-pink-500/20 to-rose-500/20",
      glow: "shadow-pink-500/10"
    },
    {
      value: "40%",
      label: "Faster Sales Response",
      description: "Instant qualification and CRM routing protocols.",
      icon: <Zap className="w-6 h-6 text-amber-400" />,
      color: "from-amber-500/20 to-yellow-500/20",
      glow: "shadow-amber-500/10"
    },
    {
      value: "100k+",
      label: "Workflow Executions",
      description: "High-scale serverless functions and production workflows.",
      icon: <Database className="w-6 h-6 text-violet-400" />,
      color: "from-violet-500/20 to-fuchsia-500/20",
      glow: "shadow-violet-500/10"
    },
    {
      value: "300+",
      label: "DSA Problems Solved",
      description: "Strong software engineering fundamentals and problem solving.",
      icon: <Code className="w-6 h-6 text-blue-400" />,
      color: "from-blue-500/20 to-sky-500/20",
      glow: "shadow-blue-500/10"
    },
    {
      value: "200k+",
      label: "Trailhead Points",
      description: "Double Star Ranger, continuously upskilling in Salesforce.",
      icon: <ShieldCheck className="w-6 h-6 text-orange-400" />,
      color: "from-orange-500/20 to-red-500/20",
      glow: "shadow-orange-500/10"
    }
  ];

  return (
    <section className="py-20 px-6 max-w-7xl mx-auto text-white">
      <div className="text-center mb-12">
        <p className="text-xs tracking-[0.35em] text-white/60 uppercase">
          Proven Track Record
        </p>
        <h2 className="mt-3 text-4xl md:text-5xl font-bold">
          Impact in <span className="bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">Numbers</span>
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto text-sm md:text-base">
          Quantifiable business value delivered through enterprise integration, custom fullstack builds, and agentic AI systems.
        </p>
      </div>

      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((m, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.05 }}
            whileHover={{ y: -5 }}
            className={`relative overflow-hidden bg-gradient-to-br ${m.color} backdrop-blur-xl border border-white/10 p-6 rounded-2xl shadow-xl hover:border-cyan-400/30 transition-all duration-300 ${m.glow}`}
          >
            {/* Top Row: Icon + Value */}
            <div className="flex items-center justify-between mb-4">
              <span className="p-3 bg-white/5 rounded-xl border border-white/10 shadow-inner">
                {m.icon}
              </span>
              <span className="text-3xl md:text-4xl font-extrabold text-white tracking-tight drop-shadow-[0_0_10px_rgba(255,255,255,0.15)]">
                {m.value}
              </span>
            </div>

            {/* Label & Description */}
            <h3 className="text-md font-bold text-white mb-1 tracking-wide">{m.label}</h3>
            <p className="text-xs text-gray-400 leading-relaxed font-medium">{m.description}</p>

            {/* Subtle bottom line visual element */}
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 to-indigo-500 opacity-20" />
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default MetricsDashboard;
